import {
  COSMOS_DEFAULT_FOOTPRINT_RADIUS,
  distributeConstellationAnchors,
} from "@chuzi/shared/realms/cosmos/components";
import type { StoryListItem } from "@chuzi/shared/types";

export type TagNebulaCluster = {
  tag: string;
  center: [number, number, number];
  radius: number;
  storyIds: string[];
};

export type TagNebulaLayout = {
  clusters: TagNebulaCluster[];
  storyPositions: Map<string, [number, number, number]>;
};

const STORY_SPREAD = 6;
const PLANE_Y_SPACING = 16;

function hashTag(tag: string): number {
  let h = 0;
  for (let i = 0; i < tag.length; i++) {
    h = (h * 31 + tag.charCodeAt(i)) >>> 0;
  }
  return h;
}

function tagHue(tag: string): string {
  const hue = hashTag(tag) % 360;
  return `hsl(${hue}, 72%, 58%)`;
}

export function tagNebulaColor(tag: string): string {
  return tagHue(tag);
}

/**
 * Lay out published stories as separate constellations on staggered Y planes so
 * the night sky stays readable. Tag nebula centers follow the placed stories.
 */
export function buildTagNebulaLayout(
  stories: StoryListItem[],
  footprintByStoryId?: Map<string, number>,
): TagNebulaLayout {
  if (stories.length === 0) {
    return { clusters: [], storyPositions: new Map() };
  }

  const anchorInputs = stories.map((story) => ({
    id: story.id,
    footprintRadius:
      footprintByStoryId?.get(story.id) ?? COSMOS_DEFAULT_FOOTPRINT_RADIUS,
  }));

  const anchors = distributeConstellationAnchors(anchorInputs);
  const planeCount = Math.max(3, Math.ceil(Math.sqrt(stories.length)));
  const storyPositions = new Map<string, [number, number, number]>();

  stories.forEach((story, index) => {
    const [x, , z] = anchors.get(story.id) ?? [0, 0, 0];
    const plane = index % planeCount;
    const y = (plane - (planeCount - 1) / 2) * PLANE_Y_SPACING;
    storyPositions.set(story.id, [x, y, z]);
  });

  const tagToStories = new Map<string, Set<string>>();
  for (const story of stories) {
    const tags = story.tags?.length ? story.tags : ["untagged"];
    for (const tag of tags) {
      const normalized = tag.trim().toLowerCase();
      if (!normalized) continue;
      if (!tagToStories.has(normalized)) tagToStories.set(normalized, new Set());
      tagToStories.get(normalized)!.add(story.id);
    }
  }

  const sortedTags = [...tagToStories.keys()].sort((a, b) => a.localeCompare(b));
  const clusters: TagNebulaCluster[] = sortedTags.map((tag) => {
    const storyIds = [...(tagToStories.get(tag) ?? [])];
    let cx = 0;
    let cy = 0;
    let cz = 0;
    let count = 0;
    for (const storyId of storyIds) {
      const pos = storyPositions.get(storyId);
      if (!pos) continue;
      cx += pos[0];
      cy += pos[1];
      cz += pos[2];
      count++;
    }
    const center: [number, number, number] =
      count > 0 ? [cx / count, cy / count, cz / count] : [0, 0, 0];
    const baseRadius = 8 + Math.sqrt(storyIds.length) * 3.2;
    const ySpread =
      storyIds.length > 1
        ? Math.max(
            ...storyIds.map((id) => Math.abs((storyPositions.get(id)?.[1] ?? 0) - center[1])),
          )
        : 0;
    return {
      tag,
      center,
      radius: baseRadius + ySpread,
      storyIds,
    };
  });

  return { clusters, storyPositions };
}

export function filterStoriesByTagQuery(
  stories: StoryListItem[],
  query: string,
): StoryListItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return stories;
  return stories.filter((story) =>
    (story.tags ?? []).some((tag) => tag.toLowerCase().includes(q)),
  );
}

export function filterTagClusters(
  clusters: TagNebulaCluster[],
  query: string,
): TagNebulaCluster[] {
  const q = query.trim().toLowerCase();
  if (!q) return clusters;
  return clusters.filter((c) => c.tag.includes(q));
}

export type MineOverviewFrame = {
  goalPos: [number, number, number];
  goalLookAt: [number, number, number];
  minCameraDistance: number;
};

/** Zoomed framing for browsing stories inside a single tier nebula — readable title overlay. */
export function computeNebulaStoryBrowseFrame(
  storyAnchor: [number, number, number],
  cluster: { center: [number, number, number]; radius: number },
): MineOverviewFrame {
  const [ax, ay, az] = storyAnchor;
  const dist = Math.max(cluster.radius * 0.42 + 10, 20);
  return {
    goalLookAt: [ax, ay, az],
    goalPos: [ax, ay + dist * 0.32, az + dist * 0.74],
    minCameraDistance: 2,
  };
}

/** Wide framing that keeps all three tier nebulae visible while centering on a story anchor. */
export function computeMineSelectionOverviewFrame(
  selectedAnchor: [number, number, number],
  privateCluster: { center: [number, number, number]; radius: number },
  limitedCluster: { center: [number, number, number]; radius: number },
  worldwideCluster: { center: [number, number, number]; radius: number },
): MineOverviewFrame {
  const clusters = [privateCluster, limitedCluster, worldwideCluster];

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  for (const cluster of clusters) {
    minX = Math.min(minX, cluster.center[0] - cluster.radius);
    maxX = Math.max(maxX, cluster.center[0] + cluster.radius);
    minZ = Math.min(minZ, cluster.center[2] - cluster.radius);
    maxZ = Math.max(maxZ, cluster.center[2] + cluster.radius);
  }

  const span = Math.max(maxX - minX, maxZ - minZ, 48);
  // Closer default than the full triple overview — still fits all three nebulae.
  const dist = Math.max(span * 0.48 + 14, 34);
  const [ax, ay, az] = selectedAnchor;

  return {
    goalLookAt: [ax, ay, az],
    goalPos: [ax, dist * 0.34, az + dist * 0.78],
    minCameraDistance: 2,
  };
}

/** Tight framing that zooms into a single constellation's scene stars. */
export function computeConstellationFocusFrame(
  scenePositions: [number, number, number][],
): MineOverviewFrame {
  if (scenePositions.length === 0) {
    return { goalLookAt: [0, 0, 0], goalPos: [0, 4, 12], minCameraDistance: 2 };
  }

  let cx = 0;
  let cy = 0;
  let cz = 0;
  for (const [x, y, z] of scenePositions) {
    cx += x;
    cy += y;
    cz += z;
  }
  cx /= scenePositions.length;
  cy /= scenePositions.length;
  cz /= scenePositions.length;

  let maxDist = 0;
  for (const [x, y, z] of scenePositions) {
    const d = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2 + (z - cz) ** 2);
    if (d > maxDist) maxDist = d;
  }

  const radius = Math.max(maxDist, 2);
  const dist = radius * 2.8 + 18;

  return {
    goalLookAt: [cx, cy, cz],
    goalPos: [cx, cy + dist * 0.25, cz + dist * 0.82],
    minCameraDistance: 2,
  };
}

export function computeTripleClusterOverviewFrame(
  privateCluster: { center: [number, number, number]; radius: number },
  limitedCluster: { center: [number, number, number]; radius: number },
  worldwideCluster: { center: [number, number, number]; radius: number },
): { goalPos: [number, number, number]; goalLookAt: [number, number, number] } {
  const clusters = [privateCluster, limitedCluster, worldwideCluster];

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  for (const cluster of clusters) {
    minX = Math.min(minX, cluster.center[0] - cluster.radius);
    maxX = Math.max(maxX, cluster.center[0] + cluster.radius);
    minZ = Math.min(minZ, cluster.center[2] - cluster.radius);
    maxZ = Math.max(maxZ, cluster.center[2] + cluster.radius);
  }

  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  const span = Math.max(maxX - minX, maxZ - minZ, 48) + 32;
  const dist = Math.max(span * 1.55, 82);

  return {
    goalLookAt: [cx, 0, cz],
    goalPos: [cx, dist * 0.38, cz + dist * 1.18],
  };
}

/** @deprecated Use computeTripleClusterOverviewFrame */
export function computeDualClusterOverviewFrame(
  published: { center: [number, number, number]; radius: number },
  unpublished: { center: [number, number, number]; radius: number },
): { goalPos: [number, number, number]; goalLookAt: [number, number, number] } {
  const cx = (published.center[0] + unpublished.center[0]) / 2;
  const cz = (published.center[2] + unpublished.center[2]) / 2;
  const span =
    Math.hypot(
      published.center[0] - unpublished.center[0],
      published.center[2] - unpublished.center[2],
    ) +
    published.radius +
    unpublished.radius +
    14;
  const dist = Math.max(span * 1.35, 58);
  return {
    goalLookAt: [cx, 0, cz],
    goalPos: [cx, dist * 0.42, cz + dist * 1.08],
  };
}

export function computeTagOverviewFrame(
  clusters: TagNebulaCluster[],
  storyPositions: Map<string, [number, number, number]>,
  visibleStoryIds: Set<string>,
  pinnedCluster?: TagNebulaCluster | null,
): { goalPos: [number, number, number]; goalLookAt: [number, number, number] } {
  const allClusters = pinnedCluster ? [pinnedCluster, ...clusters] : clusters;
  if (allClusters.length === 0) {
    return { goalPos: [0, 14, 38], goalLookAt: [0, 0, 0] };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  for (const cluster of allClusters) {
    minX = Math.min(minX, cluster.center[0] - cluster.radius);
    maxX = Math.max(maxX, cluster.center[0] + cluster.radius);
    minY = Math.min(minY, cluster.center[1] - cluster.radius * 0.35);
    maxY = Math.max(maxY, cluster.center[1] + cluster.radius * 0.35);
    minZ = Math.min(minZ, cluster.center[2] - cluster.radius);
    maxZ = Math.max(maxZ, cluster.center[2] + cluster.radius);
  }

  for (const [storyId, pos] of storyPositions) {
    if (!visibleStoryIds.has(storyId)) continue;
    minX = Math.min(minX, pos[0] - STORY_SPREAD);
    maxX = Math.max(maxX, pos[0] + STORY_SPREAD);
    minY = Math.min(minY, pos[1] - STORY_SPREAD);
    maxY = Math.max(maxY, pos[1] + STORY_SPREAD);
    minZ = Math.min(minZ, pos[2] - STORY_SPREAD);
    maxZ = Math.max(maxZ, pos[2] + STORY_SPREAD);
  }

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const cz = (minZ + maxZ) / 2;
  const span = Math.max(maxX - minX, maxZ - minZ, maxY - minY, 24);
  const dist = Math.max(span * 0.52 + 16, 32);

  return {
    goalLookAt: [cx, cy, cz],
    goalPos: [cx, cy + dist * 0.34, cz + dist * 0.78],
  };
}

/** Tight camera framing for a single tag or CHUZI nebula — matches MINE browse distance. */
export function computeClusterOverviewFrame(
  cluster: TagNebulaCluster,
): { goalPos: [number, number, number]; goalLookAt: [number, number, number] } {
  const dist = Math.max(cluster.radius * 0.55 + 16, 28);
  const [cx, cy, cz] = cluster.center;
  return {
    goalLookAt: [cx, cy, cz],
    goalPos: [cx, cy + dist * 0.34, cz + dist * 0.78],
  };
}
